import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface PieChartProps {
  principalInterest: number;
  taxes: number;
  insurance: number;
  hoa: number;
  pmi: number;
  totalPayment: number;
}

const COLORS = {
  principalInterest: 'hsl(199, 89%, 48%)', // sky-500
  taxes: 'hsl(262, 83%, 58%)', // violet-500
  insurance: 'hsl(38, 92%, 50%)', // amber-500
  hoa: 'hsl(160, 84%, 39%)', // emerald-500
  pmi: 'hsl(0, 84%, 60%)', // red-500
};

const MobileReversePitiPieChart: React.FC<PieChartProps> = ({
  principalInterest,
  taxes,
  insurance,
  hoa,
  pmi,
  totalPayment,
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const data = [
    { name: 'Principal & Interest', value: principalInterest, color: COLORS.principalInterest },
    { name: 'Property Taxes', value: taxes, color: COLORS.taxes },
    { name: 'Insurance', value: insurance, color: COLORS.insurance },
    ...(hoa > 0 ? [{ name: 'HOA', value: hoa, color: COLORS.hoa }] : []),
    ...(pmi > 0 ? [{ name: 'PMI', value: pmi, color: COLORS.pmi }] : []),
  ].filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = ((item.value / totalPayment) * 100).toFixed(1);
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground">{item.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(item.value)} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Pie Chart */}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex flex-col min-w-0">
              <span className="text-muted-foreground text-xs truncate">{item.name}</span>
              <span className="font-medium text-foreground">{formatCurrency(item.value)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="pt-3 border-t border-border">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Total PITI</span>
          <span className="text-lg font-bold text-primary">{formatCurrency(totalPayment)}</span>
        </div>
      </div>
    </div>
  );
};

export default MobileReversePitiPieChart;
